import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useMusicPlayer } from "@/contexts/MusicPlayerContext";

/**
 * MusicTab — thin UI controller over the global MusicPlayerContext. The actual
 * <audio> element lives in the provider so playback continues when the user
 * leaves this tab (V2.3.9).
 */
export default function MusicTab() {
  const {
    source, setSource, uploads, setUploads,
    tracks, current, trackIdx, setTrackIdx,
    playing, togglePlay, next, prev,
    volume, setVolume,
  } = useMusicPlayer();

  const [unlocked, setUnlocked] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => { try { const { data } = await api.get("/store/me"); setUnlocked(!!data.music_upload_unlocked); } catch {} })();
  }, []);

  function onFile(e) {
    const files = Array.from(e.target.files || []);
    if (uploads.length + files.length > 20) { setErr("Max 20 tracks"); setTimeout(() => setErr(""), 2500); return; }
    Promise.all(files.map(f => new Promise(res => {
      const r = new FileReader();
      r.onload = () => res({ id: `up-${Date.now()}-${Math.random().toString(36).slice(2,6)}`, name: f.name, url: r.result });
      r.readAsDataURL(f);
    }))).then(list => setUploads(u => [...u, ...list]));
  }

  return (
    <div data-testid="music-tab" className="space-y-4 max-w-2xl">
      <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold flex items-center justify-between">
        <span><i className="fa-solid fa-music mr-1 text-amber-400"/> Trading Soundtrack</span>
        {playing && <span className="text-emerald-300 font-bold normal-case"><i className="fa-solid fa-circle text-[6px] mr-1 animate-pulse"/>playing in background</span>}
      </div>

      {/* Source switcher */}
      <div className="flex gap-2 text-[10px] font-bold">
        <button data-testid="music-source-preset" onClick={() => setSource("preset")}
          className={`px-3 py-1.5 rounded ${source === "preset" ? "bg-amber-500/20 text-amber-200 border border-amber-500/40" : "bg-slate-800 text-slate-400 hover:text-white"}`}>
          Free Library
        </button>
        {unlocked && (
          <button data-testid="music-source-uploads" onClick={() => setSource("uploads")}
            className={`px-3 py-1.5 rounded ${source === "uploads" ? "bg-amber-500/20 text-amber-200 border border-amber-500/40" : "bg-slate-800 text-slate-400 hover:text-white"}`}>
            My Uploads
          </button>
        )}
      </div>

      {/* Track list */}
      <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4 space-y-3">
        {tracks.length === 0 ? (
          <div className="text-xs text-slate-500 py-3 text-center">No tracks in this library yet.</div>
        ) : (
          <>
            <div className="text-xs text-slate-300">
              Now playing: <span className="text-white font-bold">{current?.name || "—"}</span>
            </div>
            {err && <div className="text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1">{err}</div>}

            <div className="flex items-center gap-2">
              <button onClick={prev} className="px-2 py-1 bg-slate-800 hover:bg-slate-700 rounded text-xs"><i className="fa-solid fa-backward-step"/></button>
              <button onClick={togglePlay} data-testid="music-play" className="px-3 py-1 bg-amber-500 hover:bg-amber-400 text-slate-900 rounded text-xs font-bold">
                <i className={`fa-solid ${playing ? "fa-pause" : "fa-play"}`}/>
              </button>
              <button onClick={next} className="px-2 py-1 bg-slate-800 hover:bg-slate-700 rounded text-xs"><i className="fa-solid fa-forward-step"/></button>
              <div className="text-[10px] text-slate-500 ml-1">Track {trackIdx+1}/{tracks.length}</div>
            </div>

            {/* Volume slider */}
            <div className="pt-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase font-bold text-slate-500"><i className="fa-solid fa-volume-high mr-1 text-amber-400"/>Volume</span>
                <span data-testid="music-volume-value" className="text-[10px] text-amber-300 font-bold tabular-nums">{volume}</span>
              </div>
              <input data-testid="music-volume-slider" type="range" min={0} max={100} step={1} value={volume}
                onChange={e => setVolume(parseInt(e.target.value))}
                className="w-full accent-amber-400 cursor-pointer"/>
              <div className="flex justify-between text-[9px] text-slate-600 mt-0.5"><span>0</span><span>30 (default)</span><span>100</span></div>
            </div>

            {/* Quick track list */}
            <div className="max-h-40 overflow-y-auto space-y-1 border-t border-slate-800 pt-2">
              {tracks.map((t, i) => (
                <button key={t.id || i} onClick={() => setTrackIdx(i)}
                  className={`w-full flex items-center justify-between text-left px-2 py-1.5 rounded text-[11px] ${i === trackIdx ? "bg-amber-500/15 text-amber-200" : "bg-slate-900/40 text-slate-300 hover:bg-slate-800/60"}`}>
                  <span className="truncate flex-1">{t.name}</span>
                  {source === "uploads" && (
                    <span onClick={e => { e.stopPropagation(); setUploads(uu => uu.filter((_, j) => j !== i)); }}
                      className="ml-2 text-rose-400 cursor-pointer hover:text-rose-300"><i className="fa-solid fa-xmark"/></span>
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Upload panel */}
      <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4">
        <div className="text-sm font-bold text-white mb-1">Personal Uploads</div>
        {unlocked ? (
          <>
            <div className="text-[11px] text-slate-400 mb-2">{uploads.length}/20 tracks · Max 10MB per file</div>
            <input type="file" accept="audio/*" multiple onChange={onFile} data-testid="music-upload"
              className="text-xs text-slate-400 file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-amber-500 file:text-slate-900 file:font-bold cursor-pointer"/>
          </>
        ) : (
          <div className="text-[11px] text-slate-400">Unlock personal uploads via the Store ($10 one-time) to load your own tracks.</div>
        )}
      </div>

      <div className="text-[10px] text-slate-600 italic">
        Tip: Music keeps playing when you switch tabs or open the Sandbox — perfect background soundtrack.
      </div>
    </div>
  );
}
